<template>
  <div class="flex items-center gap-2 px-4 py-4 overflow-x-auto sm:pr-0 md:py-0 sm:overflow-visible hidden-scrollbar">
    <Button type="solid" variant="blue" icon="general.copy" :data-testid="BUTTON_TEST_ID.COPY" @click.prevent="copyAction">Copy link</Button>
    <Button type="link" variant="black" :icon="getSharingIcon()" :data-testid="BUTTON_TEST_ID.SHARE" @click.prevent="openShareModalAction">Share</Button>

    <Button
      type="link"
      variant="black"
      v-if="type === ItemType.IMAGE && isCurrentItemOwner && !isGif"
      :loading="isEditEditing"
      :disabled="isEditProcessing"
      :tooltip="isEditProcessing ? 'Processing' : ''"
      :data-testid="BUTTON_TEST_ID.ANNOTATE"
      icon="general.annotate"
      @click.prevent="!isEditProcessing ? annotateAction() : false"
      >Annotate</Button
    >

    <Button
      type="link"
      variant="black"
      v-if="type === ItemType.VIDEO && isCurrentItemOwner"
      :loading="isEditEditing"
      :disabled="isEditProcessing"
      :tooltip="isEditProcessing ? 'Processing' : ''"
      :data-testid="BUTTON_TEST_ID.EDIT"
      icon="general.pencil"
      @click.prevent="!isEditProcessing ? annotateAction() : false"
      >Edit</Button
    >
    <Button
      v-if="isDownloadVisible"
      :disabled="isEditProcessing"
      type="link"
      variant="black"
      :data-testid="BUTTON_TEST_ID.DOWNLOAD"
      icon="general.download"
      @click.prevent="downloadAction"
    >
      Download
    </Button>
    <Button v-if="!isCurrentItemOwner" type="link" variant="black" :data-testid="BUTTON_TEST_ID.SAVE" icon="general.logoBW" @click.prevent="saveAction"
      >Save</Button
    >
    <ContextMenuWrapper v-if="itemActionsMenu?.length" data-testid="viewer-more-menu">
      <Button data-testid="viewer-actions-more" text="More" type="link" variant="black" icon="general.meatballMoreDots" @click.prevent />

      <template #menu>
        <template v-for="item in itemActionsMenu" :key="item.name">
          <MenuSeparator v-if="item.isSeparator" />

          <MenuToggle v-else-if="item.isToggle" :title="item.title" :type="item.type" v-model="item.value" @click.prevent="runItemAction(item)">
            {{ item.title }}
          </MenuToggle>

          <MenuLink
            v-else
            :icon="item.icon"
            :title="item.title"
            :type="item.type"
            @click.prevent="runItemAction(item)"
            :disabled="item.disabled"
            :loading="item.loading"
          >
            <section class="flex gap-1 items-center">
              {{ item.title }}
              <ZtTooltip v-if="item.tooltip" :content="item.tooltip" placement="top">
                <Icon name="general.info" size="xs" />
              </ZtTooltip>
            </section>
          </MenuLink>
        </template>
      </template>
    </ContextMenuWrapper>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { Button } from '@/storybook/button';
import { Icon } from '@/storybook/icon';
import { ZtTooltip } from '@/storybook/tooltip';
import { ContextMenuWrapper, MenuSeparator, MenuLink, MenuToggle } from '@/storybook/menu';

import { ItemType, useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useItemActionsToolbar, BUTTON_TEST_ID } from '@/modules/viewer/composables/useItemActionsToolbar';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useItemShare } from '@/modules/viewer/composables/useItemShare';
import { useUser } from '@/modules/viewer/composables/useUser';

const { downloadAction, saveAction, openShareModalAction, copyAction, annotateAction } = useItemActions();

const viewerItemStore = useViewerItemStore();

const { itemActionsMenu } = useItemActionsToolbar();

const { downloadEnabled, isEditEditing, isEditProcessing, type, isGif } = storeToRefs(viewerItemStore);
const { isCurrentItemOwner } = useUser();

const { getSharingIcon } = useItemShare();

const isDownloadVisible = computed(() => {
  // disable download - item settings
  if (!downloadEnabled.value) {
    return false;
  }

  // disable download for video and image
  if (isCurrentItemOwner.value && type.value && [ItemType.VIDEO, ItemType.IMAGE].includes(type.value)) {
    return false;
  }

  return true;
});

const runItemAction = (item?: any) => {
  const { action } = item;

  if (typeof action === 'function') {
    return action();
  }
};
</script>
